import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Axios from 'axios';
import AppLayout from '../../components/AppLayout';
import CircularLoader from '../../components/CircularLoader/CircularLoader';
import { S, Icon, StatusBadge, formatDate } from '../regureStyles';
import { countOpen } from '../../utils/claimStatus';
import ReportClaimModal from './ReportClaimModal';

const MyClaimsScreen = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const userLogin = useSelector(state => state.userLogin);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const [reportClaimOpen, setReportClaimOpen] = useState(false);

  const user = userLogin?.userInfo?.data?.data || {};
  const apiUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    if (userLogin?.userInfo?.status !== 'success') return;

    const fetchClaims = async () => {
      try {
        setLoading(true);
        const response = await Axios.get(`${apiUrl}/me/my-claims`, { withCredentials: true });
        setClaims(response.data?.data || []);
      } catch (err) {
        setError(err.response?.data?.message || t('screens.portal.failedLoadClaims'));
      } finally {
        setLoading(false);
      }
    };

    fetchClaims();
  }, [userLogin, apiUrl]);

  if (loading) return (
    <AppLayout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularLoader />
      </div>
    </AppLayout>
  );

  // "Active" is the platform's non-terminal status set, not a list maintained
  // here. The four statuses this line used to hardcode omitted evaluation,
  // negotiation, approved, reported and reopened — 5 of the 9 — so Sofía's 4
  // live cases rendered as 1. See utils/claimStatus.js.
  const activeClaims = countOpen(claims);
  const filteredClaims = statusFilter === 'All Statuses' ? claims : claims.filter(c => c.status?.toLowerCase() === statusFilter.toLowerCase());

  return (
    <AppLayout>
      <div style={{
        flex: 1,
        overflowY: 'auto',
        background: `linear-gradient(135deg, ${S.bg} 0%, #f8fafc 100%)`,
        padding: '32px 40px',
        minHeight: '100%'
      }}>
        {/* Page Header */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            <span style={{ color: S.burgundy, fontWeight: 700 }}>{t('screens.portal.breadcrumbPortal')}</span> / {t('screens.portal.breadcrumbMyClaims')}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 28, fontWeight: 800, color: S.textPrimary, letterSpacing: '-0.5px' }}>
                {t('screens.portal.yourClaims')}
              </div>
              <div style={{ fontSize: 14, color: S.textMuted, marginTop: 6 }}>
                {t('screens.portal.yourClaimsSubtitle')}
              </div>
            </div>
            <button
              onClick={() => setReportClaimOpen(true)}
              style={{
                background: S.burgundy,
                color: '#fff',
                border: 'none',
                padding: '10px 20px',
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: '0 2px 8px rgba(128, 0, 32, 0.15)',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = '#5c001a';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(128, 0, 32, 0.25)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = S.burgundy;
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(128, 0, 32, 0.15)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <Icon name="plus" color="#fff" size={16} />
              {t('screens.portal.reportNewClaim')}
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 32 }}>
          <div style={{
            background: '#fff',
            border: `1px solid ${S.borderLight}`,
            borderRadius: 14,
            padding: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
              {t('screens.portal.activeClaims')}
            </div>
            <div style={{ fontSize: 32, fontWeight: 800, color: S.textPrimary }}>
              {activeClaims}
            </div>
            <div style={{ fontSize: 13, color: S.textMuted, marginTop: 8 }}>
              {t('screens.portal.activeClaimsSub')}
            </div>
          </div>

          <div style={{
            background: '#fff',
            border: `1px solid ${S.borderLight}`,
            borderRadius: 14,
            padding: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
              {t('screens.portal.totalFiled')}
            </div>
            <div style={{ fontSize: 32, fontWeight: 800, color: S.textPrimary }}>
              {claims.length}
            </div>
            <div style={{ fontSize: 13, color: S.textMuted, marginTop: 8 }}>
              {t('screens.portal.totalFiledSub')}
            </div>
          </div>

          <div style={{
            background: '#fff',
            border: `1px solid ${S.borderLight}`,
            borderRadius: 14,
            padding: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
              {t('screens.portal.lastUpdate')}
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: S.textPrimary }}>
              {claims.length > 0 ? formatDate(claims[0].updatedAt || claims[0].createdAt) : '—'}
            </div>
            <div style={{ fontSize: 13, color: S.textMuted, marginTop: 8 }}>
              {claims.length > 0 ? claims[0].claimNumber : '—'}
            </div>
          </div>
        </div>

        {/* Table Card */}
        {error && (
          <div style={{
            background: 'rgba(220, 38, 38, 0.08)',
            color: '#dc2626',
            padding: '14px 18px',
            borderRadius: 10,
            marginBottom: 20,
            fontSize: 13,
            border: '1px solid rgba(220, 38, 38, 0.15)'
          }}>
            {error}
          </div>
        )}

        <div style={{
          background: '#fff',
          border: `1px solid ${S.borderLight}`,
          borderRadius: 14,
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '18px 24px',
            borderBottom: `1px solid ${S.borderLight}`,
            background: S.bg
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary }}>
              {t('screens.portal.yourClaims')}
            </div>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              style={{
                padding: '8px 14px',
                border: `1px solid ${S.borderLight}`,
                borderRadius: 8,
                fontSize: 13,
                color: S.textSecondary,
                background: '#fff',
                cursor: 'pointer',
                outline: 'none',
                fontWeight: 500
              }}
            >
              <option value="All Statuses">{t('screens.portal.allStatuses')}</option>
              <option value="Open">{t('screens.portal.statusOpen')}</option>
              <option value="New">{t('screens.portal.statusNew')}</option>
              <option value="Closed">{t('screens.portal.statusClosed')}</option>
            </select>
          </div>

          {claims.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 40px', color: S.textMuted }}>
              <Icon name="inbox" color={S.borderLight} size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>{t('screens.portal.noClaims')}</div>
              <div style={{ fontSize: 14 }}>{t('screens.portal.noClaimsHint')}</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {[
                    t('screens.portal.claimsColClaimNumber'),
                    t('screens.portal.claimsColPolicy'),
                    t('screens.portal.claimsColStatus'),
                    t('screens.portal.claimsColLossDate'),
                    t('screens.portal.claimsColDescription'),
                    t('screens.portal.claimsColFiled'),
                  ].map(h => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'left',
                        padding: '14px 24px',
                        fontSize: 11,
                        fontWeight: 700,
                        color: S.textMuted,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        borderBottom: `1px solid ${S.borderLight}`,
                        background: S.bg,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredClaims.map(claim => (
                  <tr
                    key={claim._id}
                    style={{
                      borderBottom: `1px solid ${S.borderLight}`,
                      cursor: 'pointer',
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = S.bg}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    /* The route exists now (MyClaimDetailScreen), so the row
                       leads somewhere. It was previously inert: the number
                       was styled as a link and clicking it did nothing,
                       leaving a portal user unable to open their own case. */
                    onClick={() => history.push(`/portal/my-claims/${claim._id}`)}
                  >
                    <td style={{ padding: '14px 24px', fontSize: 13, color: S.burgundy, fontWeight: 700 }}>
                      {claim.claimNumber}
                    </td>
                    <td style={{ padding: '14px 24px', fontSize: 13, color: S.textPrimary, fontWeight: 600 }}>
                      {claim.policyNumber || '—'}
                    </td>
                    <td style={{ padding: '14px 24px', fontSize: 13 }}>
                      <StatusBadge status={claim.status} />
                    </td>
                    <td style={{ padding: '14px 24px', fontSize: 13, color: S.textSecondary }}>
                      {formatDate(claim.lossDate)}
                    </td>
                    <td style={{ padding: '14px 24px', fontSize: 13, color: S.textSecondary }}>
                      {claim.lossDescription || '—'}
                    </td>
                    <td style={{ padding: '14px 24px', fontSize: 13, color: S.textSecondary }}>
                      {/* FILED is a business date — when the case was
                          reported — and the detail screen already reads
                          reportedDate. This column read createdAt, the
                          ingestion time, so the list said CL-2026-06981 was
                          filed 13/08/2026 while its own detail page said
                          30/05/2026. createdAt stays system provenance and is
                          surfaced separately on the detail, never here. */}
                      {formatDate(claim.reportedDate || claim.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Report Claim Modal */}
      <ReportClaimModal
        open={reportClaimOpen}
        onClose={() => setReportClaimOpen(false)}
        onSuccess={(newClaim) => {
          // Refresh claims list
          setClaims(prev => [newClaim, ...prev]);
        }}
      />
    </AppLayout>
  );
};

export default MyClaimsScreen;
